import {AfterViewInit, Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {MatCalendarCellCssClasses} from '@angular/material/datepicker';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {faBolt, faCheck, faTrash, IconDefinition} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import {DatumFormatPipe} from 'src/app/pipes/datum-format.pipe';
import {LocalStorageService} from '../../local-storage.service';
import {Employees, EmpWeekPlan, EmpWorkPlan} from '../models/employees-model';
import {IndexedDbData} from '../models/indexedDB-model';
import {OrderListElement} from '../models/order-model';
import {TimeControlEntry} from '../models/timeControlEntry';
import {DataService} from '../services/data.service';
import {SharedDataService} from '../services/shared-data.service';
import {ToaterMessagesService} from '../services/toaster-messages.service';
import {ShowOpenDaysDialogComponent} from '../dialogs/show-open-days-dialog/show-open-days-dialog.component';
import {MessageBoxService} from 'src/app/services/message-box.service';
import {BehaviorSubject, Subject, takeUntil} from 'rxjs';
import {UserAuthenticationService} from '../../services/user-authentication.service';
import {HelperService} from '../../services/helper.service';
import {BenutzerModels} from '../../models/benutzer-models';
import UserLicenses = BenutzerModels.UserLicenses;

const LOCK_ICON_PATH = '../../../assets/icons/lock.svg';
const LOCK_OPEN_ICON_PATH = '../../../assets/icons/lock_open.svg';

@Component({
    selector: 'app-saved-worktimes', templateUrl: './saved-worktimes.component.html', styleUrls: ['./saved-worktimes.component.scss'],
})
export class SavedWorktimesComponent implements OnInit, OnDestroy, AfterViewInit {
    faCheck = faCheck;
    faBolt = faBolt;
    faTrash = faTrash;
    public IndexedDbData!: IndexedDbData;
    public filterTimeEntryByDate: TimeControlEntry[] = [];
    public userPermissions!: Employees;
    public totalTime = 0;
    public totalTimeAfterControl = 0;
    public totalTimeDifferencesExists = false;
    formValue = new UntypedFormControl('');
    public iconAction!: IconDefinition | null;
    public currentLang!: string;
    public currentLockIcon: string = LOCK_ICON_PATH;
    public isToday = false;
    public chosenDate = '';
    public day = '';
    protected savedEntries: BehaviorSubject<any[]> = new BehaviorSubject([] as any[]);
    private destroy$ = new Subject<void>();

    constructor(private router: Router,
                private localStorage: LocalStorageService,
                private shareDataService: SharedDataService,
                public dialog: MatDialog,
                private dataService: DataService,
                private userAuthService: UserAuthenticationService,
                public helperService: HelperService,
                private _datumFormatPipe: DatumFormatPipe,
                private toasterMessagesService: ToaterMessagesService,
                private messageBoxService: MessageBoxService) {
        this.helperService.setPageRefreshURL('/timeapp/workTimes').then(async () => {
            if (this.userAuthService.getCurrentAuthenticatedUser() === undefined) {
                await this.router.navigate(['']);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        Promise.all([
            this.helperService.setPageRefreshURL('/timeapp/workTimes'),
            this.loadDataFromIndexDB(),
            this.checkToday()
        ]);
    }

    formatDateAndDay(dateString: string): string {
        const date = new Date(dateString);
        const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
        return `${days[date.getDay()]}`;
    }

    async closeDay(): Promise<void> {
        if (this.hasTimeConflicts(this.savedEntries.getValue())) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.TIME_CONFLICT.text', '');
            return;
        }
        if (this.totalTime > 24) {
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.TIME_TOTAL_TOO_HIGH.text', '');
            return;
        }
        const chosenDate = new Date(this.formValue.value);
        if (await this.dataService.isDayClosed(this._datumFormatPipe.transformOnlyDate(chosenDate))) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Der Tag ist bereits abgeschlossen', 'La journée est déjà terminée', this.currentLang);
        }

        // check if there are other days that arent closed yet
        const empID = this.userAuthService.getCurrentAuthenticatedUser()?.employeeID;
        const res = await this.localStorage.get('myTimeControlEntries', 'SwissTimeEntry');
        const openEntries: Date[] = res.filter((entry: any) => {
            if (!entry.readyForExport && entry.employeeID === empID) {
                return new Date(entry.entryDate).setHours(0, 0, 0, 0) < chosenDate.setHours(0, 0, 0, 0);
            }
            return false;
        }).map((entry: any) => {
            return new Date(entry.entryDate).setHours(0, 0, 0, 0);
        });
        if (openEntries.length === 0) {
            await this.openCloseDayDialog(chosenDate);
        } else {
            this.showDaysOpenDialog([...new Set(openEntries)].reverse());
        }
    }

    ngAfterViewInit(): void {
        this.formValue.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
            next: async (res) => {
                this.day = this.formatDateAndDay(res);
                await this.timeEntryFilter(res);
                await this.setCurrentLockIcon();
            },
        });
    }

    async checkToday(): Promise<void> {
        this.chosenDate = this.dataService.getChosenDateFormatted();
        this.isToday = this.dataService.isToday;
    }

    checkDifference(prev: OrderListElement, current: OrderListElement): boolean {
        if (prev.endTime && current.startTime) {
            return (prev.endTime.getTime() - current.startTime.getTime()) >= 60000;
        } else {
            return !!(prev.originalEndTime && current.originalStartTime && prev.originalEndTime < current.originalStartTime);
        }
    }

    async setCurrentLockIcon(): Promise<void> {
        const chosenDate = new Date(this.formValue.value);
        this.dataService.setChosenDate(chosenDate);
        // Prüfen, ob der aktuelle Tag nicht bereits offen ist
        const datum: string = this._datumFormatPipe.transformOnlyDate(chosenDate);
        if (await this.dataService.isDayClosed(datum)) {
            this.currentLockIcon = LOCK_ICON_PATH;
        } else {
            this.currentLockIcon = LOCK_OPEN_ICON_PATH;
        }
    }

    async timeEntryFilter(date: string): Promise<void> {
        this.filterTimeEntryByDate = (await this.dataService.getTimeEntriesFromIndexedDB()).filter((el) => this.isSameDate(new Date(el.entryDate), new Date(date)));
        await this.filterTimeEntry(this.filterTimeEntryByDate);
    }

    isSameDate(d1: Date, d2: Date): boolean {
        return d1.getDate() == d2.getDate() && d1.getMonth() == d2.getMonth() && d1.getFullYear() == d2.getFullYear();
    }

    async filterTimeEntry(filterTimeEntryByDate: TimeControlEntry[]): Promise<void> {
        this.totalTime = 0;
        this.totalTimeAfterControl = 0;
        this.totalTimeDifferencesExists = false;
        let needToCheckTimeDifferences = true;

        const result = filterTimeEntryByDate.map((te) => {
            let icon;
            if (te.deleted) {
                icon = this.faTrash;
            } else if (te.exported) {
                icon = this.faBolt;
                // Die Zeit wurde noch nicht kontrolliert, wenn controlled auf false gesetzt ist
                // Wenn eine Zeit kontrolliert wurde im Navi und man die Kontrolle wieder entfernt,
                // dann wird controlled nie mehr auf true gewechselt
                if (!te.controlled && !te.timeAmount && te.objectID) {
                    needToCheckTimeDifferences = false;
                }
            } else if (!te.exported && te.readyForExport) {
                // Solange noch eine Position nicht exportiert ist, muss auch kein Vergleich stattfinden
                needToCheckTimeDifferences = false;
                icon = this.faCheck;
            } else {
                icon = null;
            }
            return this.createOrderListElement(te, icon);
        }).sort((a, b) => this.compareTimes(a.sortOrder, b.sortOrder, a.originalStart, b.originalStart)); // Sortieren nach Zeit

        this.savedEntries.next(result);
        /* Totalzeiten runden */
        this.totalTime = Math.round(this.totalTime * 100) / 100;
        this.totalTimeAfterControl = Math.round(this.totalTimeAfterControl * 100) / 100;
        // Wenn der Tag abgeschlossen ist, das Total vergleichen
        await this.setCurrentLockIcon();
        if (this.currentLockIcon === LOCK_ICON_PATH && needToCheckTimeDifferences && this.totalTime !== this.totalTimeAfterControl) {
            this.totalTimeDifferencesExists = true;
        }
    }

    getDescStyle(orderListElem: OrderListElement) {
        if (orderListElem) {
            if (orderListElem.deleted) {
                return 'is_deleted';
            } else if (orderListElem.objectNumber === '') {
                return 'is_no_obj';
            }
        }
        return '';
    }

    getTimeStyle(orderListElem: OrderListElement) {
        if (orderListElem) {
            if ((orderListElem.start && orderListElem.originalStart && orderListElem.start != orderListElem.originalStart) || (orderListElem.end && orderListElem.originalEnd && orderListElem.end != orderListElem.originalEnd) || (orderListElem.timeAmount && orderListElem.originalTimeAmount && orderListElem.timeAmount != orderListElem.originalTimeAmount)) {
                return 'text-decoration: line-through';
            }
        }
        return '';
    }

    getTimeRedStyle(orderListElem: OrderListElement) {
        // console.log("getTimeRedStyle - orderListElement", orderListElem);
        if (orderListElem.objectNumber === '') // Pausen haben keine ObjectNumber
        {
            return 'color: red';
        } else if ((orderListElem.start && orderListElem.originalStart && orderListElem.start != orderListElem.originalStart) || (orderListElem.end && orderListElem.originalEnd && orderListElem.end != orderListElem.originalEnd) || (orderListElem.timeAmount && orderListElem.originalTimeAmount && orderListElem.timeAmount != orderListElem.originalTimeAmount)) {
            return 'color: red';
        } else {
            return '';
        }
    }

    getDisplayStart(orderListElem: OrderListElement) {
        let displayStartTime = '';
        if (orderListElem) {
            if (orderListElem.start) {
                displayStartTime = orderListElem.start;
            } else if (orderListElem.originalStart) {
                displayStartTime = orderListElem.originalStart;
            } else if (orderListElem.timeAmount) {
                displayStartTime = orderListElem.timeAmount.toString() + 'h';
            } else if (orderListElem.originalTimeAmount) {
                displayStartTime = orderListElem.originalTimeAmount.toString() + 'h';
            }
        }
        return displayStartTime;
    }

    getDisplayEnd(orderListElem: OrderListElement) {
        let displayEndTime = '';
        if (orderListElem) {
            if (orderListElem.end) {
                displayEndTime = ' - ' + orderListElem.end + ' ' + orderListElem.timeAmount + 'h';
            } else if (orderListElem.originalEnd) {
                displayEndTime = ' - ' + orderListElem.originalEnd + ' ' + orderListElem.originalTimeAmount + 'h';
            }
        }
        return displayEndTime;
    }

    getDisplayDifferences(orderListElem: OrderListElement) {
        let displayDifferences = '';
        if (orderListElem) {
            if ((orderListElem.start && orderListElem.originalStart && orderListElem.start != orderListElem.originalStart) || (orderListElem.end && orderListElem.originalEnd && orderListElem.end != orderListElem.originalEnd) || (orderListElem.timeAmount && orderListElem.originalTimeAmount && orderListElem.timeAmount != orderListElem.originalTimeAmount)) {
                displayDifferences = '(' + orderListElem.originalStart + '-' + orderListElem.originalEnd + 'h)';
            }
        }
        return displayDifferences;
    }

    getSpace(orderListElem: OrderListElement) {
        let displayDifferences = '';
        if (orderListElem) {
            if ((orderListElem.start && orderListElem.originalStart && orderListElem.start != orderListElem.originalStart) || (orderListElem.end && orderListElem.originalEnd && orderListElem.end != orderListElem.originalEnd) || (orderListElem.timeAmount && orderListElem.originalTimeAmount && orderListElem.timeAmount != orderListElem.originalTimeAmount)) {
                displayDifferences = '  ';
            }
        }
        return displayDifferences;
    }

    compareTimes(sortOrderA: number | undefined, sortOrderB: number | undefined, timeA: string, timeB: String): number {
        if (sortOrderA && sortOrderA > 0) {
            // Sortierung aus Navi verwenden
            if (sortOrderA < sortOrderB!) {
                return -1;
            } else {
                return 1;
            }
        } else {
            // Sortierung nach Datum/Zeit
            if (timeA.length === 5 && timeB.length === 5) {
                const stundenA: number = Number(timeA.substring(0, 2));
                const stundenB: number = Number(timeB.substring(0, 2));
                if (stundenA < stundenB) {
                    return -1;
                } else if (stundenA > stundenB) {
                    return 1;
                } else {
                    // Stunden A und B sind identisch.... Minuten müssen verglichen werden
                    const minutenA: number = Number(timeA.substring(3, 5));
                    const minutenB: number = Number(timeB.substring(3, 5));
                    if (minutenA < minutenB) {
                        return -1;
                    } else if (minutenA > minutenB) {
                        return 1;
                    } else {
                        return 0;
                    }
                }
            } else {
                return 0;
            }
        }
    }

    dateClass() {
        return async (date: Date): Promise<MatCalendarCellCssClasses> => {
            date = new Date(date);
            const highlightDate = (await this.dataService.getTimeEntriesFromIndexedDB()).some((d) => {
                return (
                    new Date(d.entryDate).getDate() === new Date(date).getDate() &&
                    new Date(d.entryDate).getMonth() === new Date(date).getMonth() &&
                    new Date(d.entryDate).getFullYear() === new Date(date).getFullYear()
                );
            });
            return highlightDate ? 'swiss-date-class' : '';
        };
    }

    async timeStart(resultValue: OrderListElement) {
        if (await this.dataService.isDayClosed(this._datumFormatPipe.transformOnlyDate(new Date()))) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Der heutige Tag ist bereits abgechlossen.', 'La journée d\'aujourd\'hui est déjà terminée.', this.currentLang);
            return;
        }

        this.shareDataService.changeMessage(resultValue, 'start');
        this.dataService.setChosenDate(new Date());
        this.shareIndexDb();
        this.router.navigateByUrl('/timeapp');
    }

    copyOrder(resultValue: OrderListElement) {
        this.dataService.setChosenDate(new Date());
        this.shareDataService.changeMessage(resultValue, 'copy');
        this.shareIndexDb();
        this.router.navigateByUrl('/timeapp');
    }

    async startEmptyOrderWithPickedDate() {
        const datum: string = this._datumFormatPipe.transformOnlyDate(this.dataService.getChosenDate());
        if (await this.dataService.isDayClosed(datum)) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Der Tag ist bereits abgeschlossen', 'La journée est déjà terminée', this.currentLang);
            return;
        }
        if (this.dataService.isInThePast(this.dataService.getChosenDate()) && !this.userPermissions.allowTimerecordingInPast) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Keine Berechtigung für Zeiterfassung in der Vergangenheit', 'Pas d\'autorisation pour la saisie du temps dans le passé', this.currentLang);
            return;
        }

        try {
            this.newTimeEntryWithPickedDate();
        } catch (ex) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Eintrag kann nicht hinzugefügt werden', 'L\'entrée ne peut pas être ajoutée', this.currentLang);
        }
    }

    newTimeEntryWithPickedDate() {
        const newOrder: OrderListElement = {
            rowID: '',
            entryDate: '',
            objectNumber: '',
            objectDesc: '',
            objectID: '',
            lastName: '',
            description: '',
            start: '',
            end: '',
            originalStart: this.formValue.value,
            originalEnd: '',
            timeAmount: 0,
            costCenterID: 0,
            workplace: '',
            exported: false,
            iconAction: this.iconAction!,
            note: '',
            differenceTimeExists: false,
            expenseID: 0,
            expenseAmount: 0,
            expenseRate: 0,
            expenseDescription: '',
            deleted: false,
        };

        this.shareDataService.changeMessage(newOrder, 'new');
        this.shareIndexDb();
        this.router.navigateByUrl('/timeapp');
    }

    async editOrder(resultValue: OrderListElement) {
        if (resultValue.exported) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Bearbeitung nicht möglich, da die Zeiten bereits synchronisert sind.', 'Traitement impossible, car les temps sont déjà synchronisés.', this.currentLang);
            return;
        }

        if (await this.dataService.isDayClosed(this._datumFormatPipe.transformOnlyDate(new Date(this.formValue.value)))) {
            this.toasterMessagesService.toasterTranslateErrorMessages('Bearbeitung nicht möglich, da der Tag bereits abgechlossen ist.', 'Le traitement n\'est pas possible, car la journée est déjà clôturée.', this.currentLang);
            return;
        }

        this.shareDataService.changeMessage(resultValue, 'edit');
        this.shareIndexDb();
        this.router.navigateByUrl('/timeapp');
    }

    deleteOrder(resultValue: OrderListElement) {
        if (!resultValue.exported) {
            this.messageBoxService.askYesNoQuestion('TIMEENTRY.Eintrag löschen', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true).then(result => {
                if (result) {
                    // Eintrag in der IndexedDB löschen
                    this.dataService
                        .deleteTimeControlEntry(resultValue.rowID)
                        .then(async (x) => {
                            if (x) {
                                // Eintrag konnte gelöscht werden
                                // Tagestotal neu berechnen
                                await this.timeEntryFilter(this.formValue.value);
                                this.toasterMessagesService.toasterTranslateSuccessMessages('Eintrag wurde gelöscht', 'L\'entrée a été supprimée', this.currentLang);
                            }
                        });
                }
            });
        }
    }

    goBack() {
        this.dataService.resetChosenDate();
        this.router.navigate(['/timeapp']);
    }

    shareIndexDb() {
        this.shareDataService.shareIndexedDB(this.IndexedDbData);
    }

    filterTimeEntryAndNotifyUser(): Promise<void> {
        return new Promise(resolve => {
            setTimeout(async () => {
                let hasError = false;
                const user = this.userAuthService.getCurrentAuthenticatedUser();
                if (!user) {
                    throw Error('User not found');
                }

                if (user.orderSourceVinavonTime === 1) {
                    // Einsatzplanung
                    await this.getPlannedEmployeeWorks();
                } else if (user.orderSourceVinavonTime === 2) {
                    // Auslastungsplanung
                    await this.getPlannedEmployeeWeeks();
                }

                hasError = hasError || await this.dataService.syncImages();
                await this.timeEntryFilter(this.formValue.value);
                if (!hasError) {
                    this.toasterMessagesService.toasterTranslateSuccessMessages('Synchronisierung erfolgreich!', 'Synchronisation réussie!', this.currentLang);
                } else {
                    this.toasterMessagesService.toasterTranslateErrorMessages('Synchronisierung fehlgeschlagen!', 'Échec de la synchronisation!', this.currentLang);
                }
                resolve();
            }, 100);
        });
    }

    showDaysOpenDialog(unique: Date[]) {
        const dialogRef = this.dialog.open(ShowOpenDaysDialogComponent, {
            height: 'auto', data: {dates: unique}
        });

        dialogRef.afterClosed().subscribe({
            next: (result: any) => {
                if (result) {
                    if (result.date) {
                        this.formValue.setValue(new Date(result.date));
                    }
                }
            }
        });
    }

    async openCloseDayDialog(chosenDay: Date): Promise<void> {
        if (!navigator.onLine) {
            this.toasterMessagesService.toasterTranslateErrorMessages('keine Internetverbindung', 'pas de connexion Internet', this.currentLang);
            return;
        }

        const result = await this.messageBoxService.askYesNoQuestion('HOME.Tag abgeschlossen', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true);
        if (result) {
            this.filterTimeEntryByDate = await this.dataService.closeDay(chosenDay);
            await this.setCurrentLockIcon();
            await this.filterTimeEntry(this.filterTimeEntryByDate);
            await this.dataSync();
        }
    }

    async dataSync(): Promise<void> {
        if (!this.helperService.isWebServiceAvailable()) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Synchronisation kann nicht ohne Webservice Verbindung ausgeführt werden', 'La synchronisation ne peut pas être effectuée sans connexion au service web', this.currentLang);
        }
        const syncData = await this.messageBoxService.askYesNoQuestion('HOME.Data Sync', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true);

        // refresh localstorage 'useranwendung' to then check it
        await this.userAuthService.getUserLicenses();
        const hasMobileLicense = await this.userAuthService.hasLicences(UserLicenses.CMobileTime);
        if (!hasMobileLicense) {
            await this.messageBoxService.showErrorOK('MAIN_APP_TIME.REVOKED_LICENCES.text', 'MAIN_APP_TIME.REVOKED_LICENCES.title', ['' + this.userAuthService.getCurrentAuthenticatedUser()?.employeeID]);
        }

        try {
            this.helperService.lockApp(false, true);
            if (syncData) {
                await this.dataService.startSyncData(undefined);

                await this.getAllOrdersWebCall();
                await this.getAllEmployeesWebCall();
                await this.filterTimeEntryAndNotifyUser(); // Bilder werden hier auch mit übermittelt
            }
        } catch (ex) {
            console.error(ex);
            this.messageBoxService.showErrorByLanguageKey('MAIN_APP_TIME.SYNC_ERROR.text', '');
        } finally {
            if (!hasMobileLicense) {
                await this.userAuthService.logoutUser();
            }
            this.helperService.unlockApp();
        }
    }

    async getAllOrdersWebCall(): Promise<void> {
        const res = await this.dataService.getAllOrders();
        if (!res) {
            return;
        }
        this.IndexedDbData.orders = res;
        await this.localStorage.delete('orders', 'SwissTimeEntry', true);
        await this.localStorage.add('orders', this.IndexedDbData.orders, 'SwissTimeEntry', false, true);
    }

    async getAllEmployeesWebCall(): Promise<void> {
        const res = await this.dataService.getAllEmployee();
        if (!res) {
            return;
        }
        this.IndexedDbData.allEmployees = res;
    }

    async openDay(): Promise<void> {
        const chosenDay = new Date(this.formValue.value);

        // Prüfen, ob der aktuelle Tag nicht bereits offen ist
        if (!await this.dataService.isDayClosed(this._datumFormatPipe.transformOnlyDate(chosenDay))) {
            return this.toasterMessagesService.toasterTranslateWarnMessages('Tag ist bereits geöffnet', 'La journée est déjà ouverte', this.currentLang);
        }

        if (!this.userPermissions.dayAgainActive) {
            return this.toasterMessagesService.toasterTranslateErrorMessages('Keine Berechtigung um den Tag zu öffnen', 'Pas de permission pour rouvrir la journée', this.currentLang);
        }

        const res = await this.messageBoxService.askYesNoQuestion('HOME.Tag öffnen', '', [], [], false, 'MAIN_APP_DIALOG.confirm', 'MAIN_APP_DIALOG.cancel', true);
        if (res) {
            this.filterTimeEntryByDate = await this.dataService.openDay(chosenDay);
            await this.setCurrentLockIcon();
            await this.filterTimeEntry(this.filterTimeEntryByDate);
            this.toasterMessagesService.toasterTranslateSuccessMessages('Der Tag wurde wieder geöffnet', 'La journée a été rouverte', this.currentLang);
        }
    }

    async getPlannedEmployeeWeeks(): Promise<EmpWeekPlan[]> {
        const res = await this.dataService.getPlannedEmployeeWeeks();
        if (!res) {
            throw Error('EmpWeekPlan could not be loaded');
        }
        this.IndexedDbData.plannedEmployeeWeeks = res;
        return this.IndexedDbData.plannedEmployeeWeeks;
    }

    async previous(): Promise<void> {
        const previousDay = moment(this.formValue.value)
            .subtract(1, 'days')
            .format();
        this.formValue.setValue(new Date(previousDay));
        await this.checkToday();
    }

    async next(): Promise<void> {
        const nextDay = moment(this.formValue.value).add(1, 'days').format();
        this.formValue.setValue(new Date(nextDay));
        await this.checkToday();
    }

    // Umwandlung CRLF (\r\n) zu Zeilenumbruch
    getItemDescription(item: OrderListElement) {
        // console.log("getItemDescription", item);
        if (item) {
            if (item.objectDesc) {
                return item.objectDesc.replace('\r\n', '<br />');
            } else {
                return item.objectNumber || item.description || '';
            }
        }
        return '';
    }

    protected hasTimeConflictBetweenEntries(resultElement: TimeControlEntry | OrderListElement, item: TimeControlEntry | OrderListElement): boolean {
        if (!item.originalStartTime && !item.originalEndTime) {
            return false;
        }
        if (!resultElement || !resultElement.originalStartTime && !resultElement.originalEndTime) {
            return false;
        }
        const startTime = new Date(item.originalStartTime!);
        const endTime = new Date(item.originalStartTime!);

        const sT = new Date(resultElement.originalStartTime!);
        const eT = new Date(resultElement.originalEndTime!);

        return (startTime > sT && startTime < eT) || (endTime < eT && endTime < sT);
    }

    private async getPlannedEmployeeWorks(): Promise<EmpWorkPlan[]> {
        const res = await this.dataService.getPlannedEmployeeWorks();
        if (!res) {
            throw Error('No EmployeeWorkPlan could be found');
        }
        this.IndexedDbData.plannedEmployeeWorks = res;
        return this.IndexedDbData.plannedEmployeeWorks;
    }

    private hasTimeConflicts(timeControlEntries: TimeControlEntry[] | OrderListElement[]): boolean {
        return timeControlEntries.reduce(
            (accumulator, currentValue, index, arr) => {
                let conflictToNext = false;
                if (arr[index + 1]) {
                    conflictToNext = this.hasTimeConflictBetweenEntries(currentValue, arr[index + 1]);
                }
                return accumulator || conflictToNext;
            },
            false
        );
    }

    private async loadDataFromIndexDB(): Promise<void> {
        this.IndexedDbData = await this.localStorage.getAllDataFromIndexedDB();
        this.currentLang = this.IndexedDbData.language!;
        this.userPermissions = this.IndexedDbData.allEmployees.find((el) => el.employeeID === this.userAuthService.getCurrentAuthenticatedUser()!.employeeID)!;
        const d = this.dataService.getChosenDate();
        this.formValue.setValue(d);
    }

    private createOrderListElement(te: TimeControlEntry, icon): OrderListElement {
        let orderListElement: OrderListElement;
        try {
            /* Korrektur Objectnumber => Die Navi Zeitkontrolle speichert           Bezeichnungen in die ObjectNumber */
            const objectNumber = te.objectNumber.substring(0, te.objectNumber.indexOf(' '));
            const foundOrder = this.IndexedDbData.orders.find((el) => el.objectNumber.trim() === objectNumber.trim());

            const foundSpesen = this.IndexedDbData.spesen.find((el) => el.expenseID === te.expenseID);


            if (!te.deleted) {
                this.totalTime += parseFloat(te.originalTimeAmount as any);
                this.totalTimeAfterControl += te.timeAmount!;
            }
            orderListElement = {
                rowID: te.rowID,
                entryDate: te.entryDate,
                objectNumber: te.objectNumber,
                objectDesc: foundOrder?.objectSummary!,
                objectID: te.objectID!,
                lastName: foundOrder?.lastname!,
                description: te.description,
                originalStart: te.originalStartTime ? moment(te.originalStartTime!).format('HH:mm') : '',
                originalEnd: te.originalEndTime ? moment(te.originalEndTime!).format('HH:mm') : '',
                originalTimeAmount: te.originalTimeAmount,
                start: te.startTime ? moment(te.startTime!).format('HH:mm') : '',
                end: te.endTime ? moment(te.endTime!).format('HH:mm') : '',
                timeAmount: te.timeAmount!,
                costCenterID: te.costCenterID,
                workplace: te.workplace,
                exported: te.exported,
                iconAction: icon,
                note: te.note,
                differenceTimeExists: false,
                expenseID: te.expenseID,
                expenseDescription: foundSpesen?.description!,
                expenseAmount: te.expenseAmount,
                expenseRate: te.expenseRate,
                deleted: te.deleted,
                sortOrder: te.sortorder,
                originalStartTime: te.originalStartTime ? new Date(te.originalStartTime) : undefined,
                originalEndTime: te.originalEndTime ? new Date(te.originalEndTime) : undefined,
                startTime: te.startTime ? new Date(te.startTime) : undefined,
                endTime: te.endTime ? new Date(te.endTime) : undefined
            };
        } catch (ex) {
            console.error(ex);
            orderListElement =
                {
                    rowID: te.rowID,
                    entryDate: te.entryDate,
                    objectNumber: te.objectNumber,
                    objectDesc: 'Fehlerhafter Eintrag',
                    objectID: te.objectID!,
                    lastName: 'Fehlerhafter Eintrag',
                    description: te.description,
                    originalStart: te.originalStartTime ? moment(te.originalStartTime!).format('HH:mm') : '',
                    originalEnd: te.originalEndTime ? moment(te.originalEndTime!).format('HH:mm') : '',
                    originalTimeAmount: te.originalTimeAmount,
                    start: te.startTime ? moment(te.startTime!).format('HH:mm') : '',
                    end: te.endTime ? moment(te.endTime!).format('HH:mm') : '',
                    timeAmount: te.timeAmount!,
                    costCenterID: te.costCenterID,
                    workplace: te.workplace,
                    exported: te.exported,
                    iconAction: icon,
                    note: te.note,
                    differenceTimeExists: false,
                    expenseID: te.expenseID,
                    expenseDescription: 'Fehlerhafter Eintrag',
                    expenseAmount: te.expenseAmount,
                    expenseRate: te.expenseRate,
                    deleted: te.deleted,
                    sortOrder: te.sortorder,
                    originalStartTime: te.originalStartTime ? new Date(te.originalStartTime) : undefined,
                    originalEndTime: te.originalEndTime ? new Date(te.originalEndTime) : undefined,
                    startTime: te.startTime ? new Date(te.startTime) : undefined,
                    endTime: te.endTime ? new Date(te.endTime) : undefined
                }
            ;
        }

        return orderListElement;
    }
}
